import { Box } from "@mui/material";
import DOMPurify from "dompurify";
import { SecondaryButton } from "../buttonComponent/ButtonComponent";
import Title from "../title/Title";
import Badge from "../badge/Badge";
import { formatCapitalize } from "../../config/utilities/formatCapitalize";
import Text from "../text/Text";
import {
  auditFieldMap,
  ignoredFields,
} from "../../config/utilities/auditFieldMap";
import {
  ADMIN_MODULES_FIELD,
  getAuditRecordLabel,
  getAuditRecordName,
  getAuditRecordForAction,
  getAdminAuditFieldKeys,
  getAdminAuditFieldKeysForUpdate,
  getAdminFieldLabel,
  getAdminModuleKeysForDisplay,
  hasDisplayableAdminFieldValue,
  isAuditFieldChanged,
  isCampaignTriggeredUpdate,
  isParentPhoneNotificationsUpdate,
  isTripScheduleStudentsUpdate,
  mergeCampaignAudienceRecord,
  hasDisplayableCampaignAudience,
  normalizeAdminModuleValue,
  normalizeInstitutionIds,
  partitionAssociatedChildsChanges,
  formatTripSessionLabel,
} from "../../config/utilities/auditLogHelpers";
import ArrowRight from "./img/arrowRight.svg";
import FirebaseImage from "../../config/utilities/FirebaseImage";
import {
  formatDateTime,
  formatETATime,
  formatTo12HourTime,
} from "../../config/utilities/fDate";
import { useTimezone } from "../../config/utilities/useTimezone";
import AuditLogIcon from "../../pages/sidebar/img/Audit.svg";
import CampaignAudienceAuditDisplay from "./CampaignAudienceAuditDisplay";
import "./DeleteModal.css";
import "./AuditLogsModal.css";

const boxStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
  maxHeight: "92vh",
  overflow: "hidden",
};

const getActionBadgeProps = (actionType) => {
  switch (actionType) {
    case "CREATE":
      return { background: "#ECFDF3", color: "#027A48" };
    case "UPDATE":
      return { background: "#EFF8FF", color: "#175CD3" };
    case "DELETE":
      return { background: "#FEF3F2", color: "#B42318" };
    default:
      return { background: "#F2F4F7", color: "#344054" };
  }
};

const formatModuleLabel = (module) => {
  if (module === "families") return "Family";
  if (module === "childs") return "Children";
  const singular =
    module?.endsWith("s") && module !== "childs"
      ? module.slice(0, -1)
      : module;
  return formatCapitalize(singular || "Record");
};

const CHILD_PREFERENCE_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
];

const CHILD_PREFERENCE_DAY_LABELS = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
};

const CHILD_PREFERENCE_SLOTS = [
  { key: "am", label: "AM" },
  { key: "pm", label: "PM" },
  { key: "eca", label: "ECA" },
  { key: "5pm", label: "5PM" },
  { key: "7pm", label: "7PM" },
];

const formatAlightPermission = (value) => {
  if (value === true || value === "true") return "Yes";
  if (value === false || value === "false") return "No";
  return formatCapitalize(String(value));
};

const formatContentType = (type) => {
  switch (type) {
    case "service-announcement":
      return "Service Announcement";
    case "bus-policy":
      return "Bus Policy";
    case "general-policy":
      return "General Policy";
    default:
      return formatCapitalize(String(type || "").replace(/-/g, " "));
  }
};

const formatContentHtml = (value) => {
  if (!value || typeof value !== "string") return null;
  if (/<[a-z][\s\S]*>/i.test(value)) {
    return (
      <div
        className="audit-log-campaign-message"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
      />
    );
  }
  return value;
};

const hasActiveChildPreferences = (preferences) =>
  CHILD_PREFERENCE_DAYS.some((day) =>
    CHILD_PREFERENCE_SLOTS.some((slot) => preferences?.[day]?.[slot.key]),
  );

const renderChildPreferencesGrid = (preferences) => {
  if (!preferences || typeof preferences !== "object") return "N/A";
  if (!hasActiveChildPreferences(preferences)) {
    return "No preferences selected";
  }

  return (
    <div className="audit-log-preferences-grid">
      <table className="audit-log-preferences-table">
        <thead>
          <tr>
            <th scope="col" className="audit-log-preferences-slot-header" />
            {CHILD_PREFERENCE_DAYS.map((day) => (
              <th key={day} scope="col" className="audit-log-preferences-day-header">
                {CHILD_PREFERENCE_DAY_LABELS[day]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CHILD_PREFERENCE_SLOTS.map((slot) => (
            <tr key={slot.key}>
              <th scope="row" className="audit-log-preferences-slot-header">
                {slot.label}
              </th>
              {CHILD_PREFERENCE_DAYS.map((day) => {
                const isActive = Boolean(preferences[day]?.[slot.key]);
                return (
                  <td
                    key={`${day}-${slot.key}`}
                    className={`audit-log-preferences-cell${
                      isActive ? " audit-log-preferences-cell--active" : ""
                    }`}
                  >
                    {isActive ? "●" : "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const hasDisplayableFieldValue = (data, field, module) => {
  if (module === "admins") {
    return hasDisplayableAdminFieldValue(data, field);
  }
  if (module === "trips" && field === "triptypeName") {
    return Boolean(data?.triptypeName ?? data?.tripTypeName);
  }
  if (module === "deeplinks" && field === "institutionIds") {
    return normalizeInstitutionIds(data?.[field]).length > 0;
  }
  if (module === "campaigns" && field === "targetAudience") {
    return hasDisplayableCampaignAudience(data);
  }
  const value = data?.[field];
  if (typeof value === "boolean") return true;
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

const getChangedFieldKeys = (before, after, module, ignored = []) => {
  const fieldKeys =
    module === "admins"
      ? getAdminAuditFieldKeysForUpdate(before, after)
      : Object.keys(auditFieldMap[module] || {});

  return fieldKeys.filter((field) => {
    if (ignored.includes(field)) return false;
    return isAuditFieldChanged(module, field, before, after);
  });
};

const renderPrimitiveValue = (displayValue) => {
  if (displayValue === null || displayValue === undefined) return null;
  if (typeof displayValue === "string" || typeof displayValue === "number") {
    return (
      <Text size="0.875rem" color="#101828" weight="500" text={String(displayValue)} />
    );
  }
  return displayValue;
};

export const AuditLogsModal = ({ selectedData, isOpen, onClose }) => {
  const { timezone } = useTimezone();

  if (!isOpen || !selectedData) return null;

  const { actionType, module, changes } = selectedData;
  const fieldMap = auditFieldMap[module] || {};
  const ignored = ignoredFields[module]?.[actionType] || [];

  const recordForTitle = getAuditRecordForAction(actionType, changes);
  const recordName = getAuditRecordName(module, recordForTitle);
  const recordLabel = getAuditRecordLabel(module, recordForTitle);
  const moduleLabel = formatModuleLabel(module);

  const isChildScheduleUpdate =
    module === "schedules" &&
    actionType === "UPDATE" &&
    Boolean(changes?.after?.childName);

  const isTripScheduleStudentsUpdateEvent =
    module === "schedules" &&
    actionType === "UPDATE" &&
    isTripScheduleStudentsUpdate(
      module,
      actionType,
      changes?.before,
      changes?.after,
    );

  const isCampaignTriggered =
    module === "campaigns" &&
    actionType === "UPDATE" &&
    isCampaignTriggeredUpdate(
      module,
      actionType,
      changes?.before,
      changes?.after,
    );

  const getTitle = () => {
    if (isChildScheduleUpdate) {
      return "Updated child schedule";
    }
    if (isTripScheduleStudentsUpdateEvent) {
      return "Updated trip schedule";
    }
    if (isCampaignTriggered) {
      return "Campaign last triggered";
    }

    switch (actionType) {
      case "CREATE":
        return `Created ${moduleLabel.toLowerCase()}`;
      case "UPDATE":
        return `Updated ${moduleLabel.toLowerCase()}`;
      case "DELETE":
        return `Deleted ${moduleLabel.toLowerCase()}`;
      default:
        return moduleLabel;
    }
  };

  const getSubtitle = () => {
    switch (actionType) {
      case "CREATE":
        return "Values set when this record was created.";
      case "UPDATE":
        if (isCampaignTriggered) {
          return "This campaign was sent to its target audience.";
        }
        return isChildScheduleUpdate
          ? "Schedule statuses that changed for this child."
          : isTripScheduleStudentsUpdateEvent
            ? "Student statuses that changed for this trip."
            : "Fields that changed in this update.";
      case "DELETE":
        return "Values that were removed with this record.";
      default:
        return "Details for this audit event.";
    }
  };

  const { formattedDate, formattedTime } = formatDateTime(
    selectedData.timestamp,
    timezone,
  );

  const formatTripStopEta = (eta) => {
    if (!eta) return "No ETA set";
    const etaStr = String(eta).trim();
    if (/AM|PM/i.test(etaStr)) {
      const { formattedTime } = formatETATime(etaStr);
      if (formattedTime && formattedTime !== "-") return formattedTime;
      return etaStr;
    }
    return formatTo12HourTime(etaStr) || etaStr;
  };

  const formatTripStopStudents = (studentAmount) => {
    if (studentAmount === undefined || studentAmount === null || studentAmount === "") {
      return null;
    }
    const count = Number(studentAmount);
    if (Number.isNaN(count)) return String(studentAmount);
    return `${count} ${count === 1 ? "student" : "students"}`;
  };

  const getTripStopDetailLine = (stop) => {
    const parts = [];
    if (stop.eta) {
      const eta = formatTripStopEta(stop.eta);
      if (eta && eta !== "No ETA set") parts.push(eta);
    }
    const students = formatTripStopStudents(stop.studentAmount);
    if (students) parts.push(students);
    return parts.join(" · ");
  };

  const formatStudentStatus = (student) => {
    const raw = student?.status ?? "N/A";
    return typeof raw === "string"
      ? formatCapitalize(raw)
      : String(raw);
  };

  const renderAffectedStudentsList = (students) => {
    if (!Array.isArray(students) || students.length === 0) {
      return "No students affected";
    }

    const ordered = [...students].sort((a, b) =>
      String(a?.name ?? "").localeCompare(String(b?.name ?? "")),
    );

    return (
      <div className="audit-log-field-value audit-log-trip-stops">
        {ordered.map((student, index) => (
          <div
            key={student?.childId || `${student?.name}-${index}`}
            className="audit-log-trip-stop-item"
          >
            <Text
              size="0.875rem"
              color="#101828"
              weight="500"
              text={`${index + 1}. ${student?.name || "Unknown student"}`}
            />
            <Text
              size="0.75rem"
              color="#667085"
              weight="400"
              text={formatStudentStatus(student)}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderAssociatedChildsList = (children) => {
    if (!Array.isArray(children) || children.length === 0) {
      return "No associated students";
    }

    const ordered = [...children].sort((a, b) =>
      String(a?.childName ?? a?.name ?? "").localeCompare(
        String(b?.childName ?? b?.name ?? ""),
      ),
    );

    return (
      <div className="audit-log-field-value audit-log-trip-stops">
        {ordered.map((child, index) => (
          <div
            key={child?.childId || `${child?.childName ?? child?.name}-${index}`}
            className="audit-log-trip-stop-item"
          >
            <Text
              size="0.875rem"
              color="#101828"
              weight="500"
              text={`${index + 1}. ${child?.childName || child?.name || "Unknown student"}`}
            />
            <Text
              size="0.75rem"
              color="#667085"
              weight="400"
              text={`Trip Session: ${formatTripSessionLabel(child?.tripSession)}`}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderTripStopsList = (stops) => {
    if (!Array.isArray(stops) || stops.length === 0) {
      return "No stops included";
    }

    const orderedStops = [...stops].sort((a, b) => {
      const seqA = a.sequence ?? Number.MAX_SAFE_INTEGER;
      const seqB = b.sequence ?? Number.MAX_SAFE_INTEGER;
      if (seqA !== seqB) return seqA - seqB;
      return String(a.name ?? "").localeCompare(String(b.name ?? ""));
    });

    return (
      <div className="audit-log-field-value audit-log-trip-stops">
        {orderedStops.map((stop, index) => {
          const detailLine = getTripStopDetailLine(stop);

          return (
          <div key={stop.id || `${stop.name}-${index}`} className="audit-log-trip-stop-item">
            <Text
              size="0.875rem"
              color="#101828"
              weight="500"
              text={`${stop.sequence ?? index + 1}. ${stop.name || "Unnamed stop"}`}
            />
            {detailLine ? (
              <Text
                size="0.75rem"
                color="#667085"
                weight="400"
                text={detailLine}
              />
            ) : null}
          </div>
          );
        })}
      </div>
    );
  };

  const renderInstitutionIdsList = (institutionIds) => {
    const ids = [...normalizeInstitutionIds(institutionIds)].sort((a, b) =>
      a.localeCompare(b),
    );

    if (ids.length === 0) {
      return "No institutions linked";
    }

    return (
      <div className="audit-log-field-value audit-log-trip-stops">
        {ids.map((id, index) => (
          <div key={`${id}-${index}`} className="audit-log-trip-stop-item">
            <Text
              size="0.875rem"
              color="#101828"
              weight="500"
              text={`${index + 1}. ${id}`}
            />
          </div>
        ))}
      </div>
    );
  };

  const createFieldFormatters = () => ({
    drivers: (field, value, record) => {
      if (field === "passwordChange") {
        return value === true ? "Yes" : "No";
      }
      if (!value) return null;
      switch (field) {
        case "type":
          return formatCapitalize(value);
        case "phoneNumber":
          return `+${value}`;
        case "profileImage":
          return <FirebaseImage location={value} />;
        default:
          return null;
      }
    },
    campaigns: (field, value, record) => {
      switch (field) {
        case "message":
          if (!value || typeof value !== "string") return null;
          return (
            <div
              className="audit-log-campaign-message"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
            />
          );
        case "targetAudience":
          return hasDisplayableCampaignAudience(record) ? (
            <CampaignAudienceAuditDisplay record={record} />
          ) : null;
        case "lastTriggered": {
          if (!value) return null;
          const { formattedDate: d, formattedTime: t } = formatDateTime(
            value,
            timezone,
          );
          return `${t} on ${d}`;
        }
        case "alertTime": {
          if (!value) return null;
          if (value?.scheduled?.isEnabled && value?.scheduled?.datetime) {
            const { formattedDate: d, formattedTime: t } = formatDateTime(
              value.scheduled.datetime,
              timezone,
            );
            return `${t} on ${d}`;
          }
          return null;
        }
        default:
          return null;
      }
    },
    parents: (field, value, record) => {
      if (!value) return null;
      if (field === "phoneNumber") {
        const callingCode = record?.callingCode
          ? `+${record.callingCode}`
          : "+60";
        return `${callingCode}${value}`;
      }
      if (field === "emailOnboardStatus") {
        return formatCapitalize(String(value));
      }
      return null;
    },
    vehicles: (field, value) => {
      if (field === "useTelematic") {
        return value === true ? "Yes" : "No";
      }
      return null;
    },
    schedules: (field, value, record, currentActionType) => {
      if (field === "stopsArray") return renderTripStopsList(value);
      if (field === "affectedStudents") return renderAffectedStudentsList(value);
      if (field === "startTime" && value) return formatTo12HourTime(value);
      if (!value) return null;
      switch (field) {
        case "tripDetails":
          return value.tripName;
        case "driverDetails":
          return value.driverName;
        case "assistantDetails":
          return value.assistantName;
        case "vehicleDetails":
          return value.vehicleNumber;
        case "schedules": {
          if (!Array.isArray(value) || value.length === 0) return null;

          const showScheduleDate =
            currentActionType === "UPDATE" && Boolean(record?.childName);

          return (
            <div className="audit-log-field-value">
              {value.map((schedule, index) => {
                const rawStatus =
                  schedule.status ??
                  schedule.scheduleStatus ??
                  schedule.state ??
                  "N/A";
                const statusText =
                  typeof rawStatus === "string"
                    ? formatCapitalize(rawStatus)
                    : String(rawStatus);

                let detailLine = statusText;

                if (showScheduleDate) {
                  const rawDate =
                    schedule.scheduleDate ??
                    schedule.scheduledDate ??
                    schedule.date ??
                    null;

                  if (rawDate) {
                    const { formattedDate: scheduleDate } = formatDateTime(
                      rawDate,
                      timezone,
                    );
                    if (scheduleDate && scheduleDate !== "-") {
                      detailLine = `${statusText} • ${scheduleDate}`;
                    }
                  }
                }

                return (
                  <div
                    key={schedule.scheduleId || index}
                    className="audit-log-schedule-item"
                  >
                    <Text
                      size="0.875rem"
                      weight="500"
                      color="#101828"
                      text={
                        schedule.scheduleName ||
                        schedule.name ||
                        "Unnamed schedule"
                      }
                    />
                    <Text
                      size="0.75rem"
                      weight="400"
                      color="#667085"
                      text={detailLine}
                    />
                  </div>
                );
              })}
            </div>
          );
        }
        default:
          return null;
      }
    },
    stops: (field, value) => {
      if (field === "associatedChilds") return renderAssociatedChildsList(value);
      return null;
    },
    trips: (field, value) => {
      if (field === "stopsArray") return renderTripStopsList(value);
      if (field === "startTime" && value) return formatTo12HourTime(value);
      return null;
    },
    deeplinks: (field, value) => {
      if (field === "institutionIds") return renderInstitutionIdsList(value);
      return null;
    },
    contents: (field, value) => {
      switch (field) {
        case "type":
          return value ? formatContentType(value) : null;
        case "content":
          return formatContentHtml(value);
        case "isActive":
          return formatAlightPermission(value);
        case "icon":
          return value ? formatCapitalize(String(value)) : null;
        default:
          return null;
      }
    },
    childs: (field, value) => {
      if (field === "alightPermission") {
        return formatAlightPermission(value);
      }
      if (value === null || value === undefined || value === "") return null;

      switch (field) {
        case "grade":
          return formatCapitalize(String(value));
        case "dob": {
          const dateStr = value.toString();
          if (dateStr.startsWith("1970-01-01")) return "N/A";
          const { formattedDate: dob } = formatDateTime(value, timezone);
          return dob || "N/A";
        }
        case "childImg":
          return <FirebaseImage location={value} />;
        case "preferences":
          return renderChildPreferencesGrid(value);
        default:
          return null;
      }
    },
  });

  const formatValue = (value, field, sourceRecord) => {
    if (module === "admins") {
      const adminData = sourceRecord?.admin || {};
      const privilegeData = sourceRecord?.privilege || {};

      switch (field) {
        case "firstName":
          return adminData.firstName ?? "N/A";
        case "lastName":
          return adminData.lastName ?? "N/A";
        case "email":
          return adminData.email ?? "N/A";
        case "role":
          return formatCapitalize(privilegeData.role ?? "N/A");
        default:
          return null;
      }
    }

    const customFormatter = createFieldFormatters()[module];
    if (customFormatter) {
      const formatted = customFormatter(
        field,
        value,
        sourceRecord,
        actionType,
      );
      if (formatted != null) return formatted;
    }

    if (field === "status" && typeof value === "string") {
      return formatCapitalize(value);
    }

    if (
      module === "stops" &&
      field === "location" &&
      value &&
      typeof value === "object" &&
      Array.isArray(value.coordinates)
    ) {
      return value.coordinates.join(", ");
    }

    if (typeof value === "object" && value !== null) {
      return null;
    }

    return String(value ?? "N/A");
  };

  const getFieldValue = (data, field) => {
    if (module === "admins") return data;
    if (module === "trips" && field === "triptypeName") {
      return data?.triptypeName ?? data?.tripTypeName;
    }
    return data?.[field];
  };

  const renderChildScheduleStatusChanges = () => {
    const beforeSchedules = changes?.before?.schedules;
    const afterSchedules = changes?.after?.schedules;

    if (!Array.isArray(beforeSchedules) || !Array.isArray(afterSchedules)) {
      return null;
    }

    const keyFor = (schedule) =>
      schedule?.scheduleId ||
      `${schedule?.scheduleName ?? ""}|${
        schedule?.scheduleDate ??
        schedule?.scheduledDate ??
        schedule?.date ??
        ""
      }`;

    const beforeByKey = new Map(
      beforeSchedules.map((schedule) => [keyFor(schedule), schedule]),
    );

    const items = afterSchedules
      .map((afterSchedule, index) => {
        const key = keyFor(afterSchedule) || String(index);
        const beforeSchedule = beforeByKey.get(key);

        const scheduleName =
          afterSchedule?.scheduleName ||
          beforeSchedule?.scheduleName ||
          afterSchedule?.name ||
          beforeSchedule?.name ||
          "Unnamed schedule";

        const rawDate =
          afterSchedule?.scheduleDate ??
          beforeSchedule?.scheduleDate ??
          afterSchedule?.scheduledDate ??
          beforeSchedule?.scheduledDate ??
          afterSchedule?.date ??
          beforeSchedule?.date ??
          null;

        const { formattedDate: scheduleDate } = rawDate
          ? formatDateTime(rawDate, timezone)
          : { formattedDate: "" };

        const formatStatus = (schedule) => {
          const raw =
            schedule?.status ??
            schedule?.scheduleStatus ??
            schedule?.state ??
            "N/A";
          return typeof raw === "string"
            ? formatCapitalize(raw)
            : String(raw);
        };

        const beforeStatus = formatStatus(beforeSchedule);
        const afterStatus = formatStatus(afterSchedule);

        if (beforeStatus === afterStatus) return null;

        const titleLine =
          scheduleDate && scheduleDate !== "-"
            ? `${scheduleName} • ${scheduleDate}`
            : scheduleName;

        return (
          <div key={afterSchedule?.scheduleId || key} className="audit-log-schedule-item">
            <Text size="0.875rem" weight="500" color="#101828" text={titleLine} />
            {renderDiffBoxes(beforeStatus, afterStatus)}
          </div>
        );
      })
      .filter(Boolean);

    return items;
  };

  // Schedule-side exempt/schedule update (PATCH /schedules): per-student status change
  // for a single schedule. Inverse of renderChildScheduleStatusChanges (per-schedule for
  // a single child). Reuses the same diff-box UI for visual consistency.
  const renderAffectedStudentsChanges = () => {
    const beforeStudents = changes?.before?.affectedStudents;
    const afterStudents = changes?.after?.affectedStudents;

    if (!Array.isArray(afterStudents) || afterStudents.length === 0) {
      return null;
    }

    const keyFor = (student) =>
      student?.childId || student?.id || student?.name || "";

    const beforeByKey = new Map(
      (Array.isArray(beforeStudents) ? beforeStudents : []).map((student) => [
        keyFor(student),
        student,
      ]),
    );

    const formatStatus = (student) => {
      const raw = student?.status ?? "N/A";
      return typeof raw === "string" ? formatCapitalize(raw) : String(raw);
    };

    const items = afterStudents
      .map((afterStudent, index) => {
        const key = keyFor(afterStudent) || String(index);
        const beforeStudent = beforeByKey.get(key);

        const name =
          afterStudent?.name || beforeStudent?.name || "Unknown student";

        const beforeStatus = formatStatus(beforeStudent);
        const afterStatus = formatStatus(afterStudent);

        if (beforeStatus === afterStatus) return null;

        return (
          <div
            key={afterStudent?.childId || key}
            className="audit-log-schedule-item"
          >
            <Text size="0.875rem" weight="500" color="#101828" text={name} />
            {renderDiffBoxes(beforeStatus, afterStatus)}
          </div>
        );
      })
      .filter(Boolean);

    return items;
  };

  const renderAdminModulesList = (record, moduleKeys) => {
    if (!record || moduleKeys.length === 0) return null;
    const modules = record?.privilege?.modules || {};

    return (
      <div className="audit-log-admin-modules">
        {moduleKeys.map((key) => (
          <Text
            key={key}
            size="0.875rem"
            color="#101828"
            weight="500"
            text={`${getAdminFieldLabel(key)}: ${
              normalizeAdminModuleValue(modules[key]) ? "Yes" : "No"
            }`}
          />
        ))}
      </div>
    );
  };

  const renderAdminModulesField = (
    field,
    variant,
    { beforeData = null, afterData = null, sourceRecord = null } = {},
  ) => {
    if (module !== "admins" || field !== ADMIN_MODULES_FIELD) return null;

    const moduleKeys = getAdminModuleKeysForDisplay(
      beforeData || sourceRecord,
      afterData || sourceRecord,
      actionType,
    );
    if (moduleKeys.length === 0) return null;

    if (variant === "update") {
      return renderUpdateField(
        field,
        renderAdminModulesList(beforeData, moduleKeys),
        renderAdminModulesList(afterData, moduleKeys),
      );
    }

    const content = renderAdminModulesList(sourceRecord, moduleKeys);
    if (!content) return null;
    return renderSingleValueField(field, content, variant);
  };

  const getFieldsForAction = (record, before, after) => {
    if (module === "admins") {
      if (actionType === "UPDATE") {
        return getAdminAuditFieldKeysForUpdate(before, after);
      }
      return getAdminAuditFieldKeys(record);
    }
    return Object.keys(fieldMap);
  };

  const renderFieldLabel = (field) =>
    module === "admins"
      ? getAdminFieldLabel(field)
      : fieldMap[field] || formatCapitalize(field);

  const renderSingleValueField = (field, displayValue, variant) => (
    <div key={field} className={`audit-log-field audit-log-field--${variant}`}>
      <div className="audit-log-field-label">{renderFieldLabel(field)}</div>
      <div className="audit-log-field-value">
        {renderPrimitiveValue(displayValue)}
      </div>
    </div>
  );

  const renderDiffBoxes = (beforeValue, afterValue) => (
    <div className="audit-log-field-diff">
      <div className="audit-log-diff-box audit-log-diff-box--before">
        <span className="audit-log-diff-label">Before</span>
        <div className="audit-log-diff-value">
          {renderPrimitiveValue(beforeValue)}
        </div>
      </div>
      <div className="audit-log-diff-arrow" aria-hidden="true">
        <img src={ArrowRight} alt="" />
      </div>
      <div className="audit-log-diff-box audit-log-diff-box--after">
        <span className="audit-log-diff-label">After</span>
        <div className="audit-log-diff-value">
          {renderPrimitiveValue(afterValue)}
        </div>
      </div>
    </div>
  );

  const renderOccurredField = (field, displayValue, variant = "update") => (
    <div
      key={field}
      className={`audit-log-field audit-log-field--${variant}`}
    >
      <div className="audit-log-field-label">{renderFieldLabel(field)}</div>
      <div className="audit-log-field-value">
        {renderPrimitiveValue(displayValue)}
      </div>
    </div>
  );

  const renderUpdateField = (field, beforeValue, afterValue) => (
    <div key={field} className="audit-log-field audit-log-field--update">
      <div className="audit-log-field-label">{renderFieldLabel(field)}</div>
      {renderDiffBoxes(beforeValue, afterValue)}
    </div>
  );

  const renderChanges = () => {
    if (actionType === "CREATE") {
      const afterData = changes?.after;
      if (!afterData) {
        return <p className="audit-logs-empty">No data available for this event.</p>;
      }

      const fields = getFieldsForAction(afterData, null, null)
        .filter((field) => !ignored.includes(field))
        .filter((field) => hasDisplayableFieldValue(afterData, field, module));

      const rows = fields
        .map((field) => {
          const modulesRow = renderAdminModulesField(field, "create", {
            sourceRecord: afterData,
            afterData,
          });
          if (modulesRow) return modulesRow;

          const sourceRecord = afterData;
          const value = getFieldValue(afterData, field);
          const displayValue = formatValue(value, field, sourceRecord);
          if (displayValue === null) return null;
          return renderSingleValueField(field, displayValue, "create");
        })
        .filter(Boolean);

      if (rows.length === 0) {
        return <p className="audit-logs-empty">No field details available.</p>;
      }

      return <div className="audit-logs-changes">{rows}</div>;
    }

    if (actionType === "DELETE") {
      const beforeData = changes?.before || changes?.after;
      if (!beforeData) {
        return <p className="audit-logs-empty">No data available for this event.</p>;
      }

      const fields = getFieldsForAction(beforeData, null, null)
        .filter((field) => !ignored.includes(field))
        .filter((field) => hasDisplayableFieldValue(beforeData, field, module));

      const rows = fields
        .map((field) => {
          const modulesRow = renderAdminModulesField(field, "delete", {
            sourceRecord: beforeData,
            beforeData,
          });
          if (modulesRow) return modulesRow;

          const sourceRecord = beforeData;
          const value = getFieldValue(beforeData, field);
          const displayValue = formatValue(value, field, sourceRecord);
          if (displayValue === null) return null;
          return renderSingleValueField(field, displayValue, "delete");
        })
        .filter(Boolean);

      if (rows.length === 0) {
        return <p className="audit-logs-empty">No field details available.</p>;
      }

      return <div className="audit-logs-changes">{rows}</div>;
    }

    if (actionType === "UPDATE") {
      const beforeData = changes?.before;
      const afterData = changes?.after;

      if (isParentPhoneNotificationsUpdate(module, actionType, afterData)) {
        return (
          <div className="audit-logs-changes">
            {renderOccurredField("phoneNotifications", "On", "occurred")}
          </div>
        );
      }

      if (
        isCampaignTriggeredUpdate(module, actionType, beforeData, afterData) &&
        afterData?.lastTriggered
      ) {
        const { formattedDate, formattedTime } = formatDateTime(
          afterData.lastTriggered,
          timezone,
        );
        return (
          <div className="audit-logs-changes">
            {renderOccurredField(
              "lastTriggered",
              `${formattedTime} on ${formattedDate}`,
              "occurred",
            )}
          </div>
        );
      }

      if (!beforeData && afterData) {
        const fields = getFieldsForAction(afterData, beforeData, afterData)
          .filter((field) => !ignored.includes(field))
          .filter((field) => hasDisplayableFieldValue(afterData, field, module));

        const rows = fields
          .map((field) => {
            const modulesRow = renderAdminModulesField(field, "create", {
              sourceRecord: afterData,
              afterData,
            });
            if (modulesRow) return modulesRow;

            const displayValue = formatValue(
              getFieldValue(afterData, field),
              field,
              afterData,
            );
            if (displayValue === null) return null;
            return renderSingleValueField(field, displayValue, "create");
          })
          .filter(Boolean);

        return rows.length > 0 ? (
          <div className="audit-logs-changes">{rows}</div>
        ) : (
          <p className="audit-logs-empty">No changes detected.</p>
        );
      }

      if (!afterData && beforeData) {
        const fields = getFieldsForAction(beforeData, beforeData, afterData).filter(
          (field) => hasDisplayableFieldValue(beforeData, field, module),
        );

        const rows = fields
          .map((field) => {
            const modulesRow = renderAdminModulesField(field, "delete", {
              sourceRecord: beforeData,
              beforeData,
            });
            if (modulesRow) return modulesRow;

            const displayValue = formatValue(
              getFieldValue(beforeData, field),
              field,
              beforeData,
            );
            if (displayValue === null) return null;
            return renderSingleValueField(field, displayValue, "delete");
          })
          .filter(Boolean);

        return rows.length > 0 ? (
          <div className="audit-logs-changes">{rows}</div>
        ) : (
          <p className="audit-logs-empty">No changes detected.</p>
        );
      }

      const changedFields = getChangedFieldKeys(
        beforeData,
        afterData,
        module,
        ignored,
      );

      if (changedFields.length === 0) {
        return <p className="audit-logs-empty">No changes detected.</p>;
      }

      // Schedule exempt/schedule update: show the total-count change and the
      // per-student status changes together under one "Affected Students" view.
      const affectedStudentItems =
        module === "schedules" ? renderAffectedStudentsChanges() : null;
      const hasAffectedStudents =
        Array.isArray(affectedStudentItems) && affectedStudentItems.length > 0;

      const rows = changedFields
        .flatMap((field) => {
          // Total is folded into the Affected Students view below — skip the standalone row.
          if (
            module === "schedules" &&
            field === "totalStudents" &&
            hasAffectedStudents
          ) {
            return [];
          }

          if (isChildScheduleUpdate && field === "schedules") {
            const scheduleChanges = renderChildScheduleStatusChanges();
            if (!scheduleChanges || scheduleChanges.length === 0) return [];

            return [
              <div key={field} className="audit-log-field audit-log-field--update">
                <div className="audit-log-field-label">
                  {renderFieldLabel(field)}
                </div>
                <div className="audit-log-field-value">{scheduleChanges}</div>
              </div>,
            ];
          }

          if (module === "schedules" && field === "affectedStudents") {
            if (!hasAffectedStudents) return [];

            const beforeTotal = beforeData?.totalStudents;
            const afterTotal = afterData?.totalStudents;
            const hasAfterTotal = typeof afterTotal === "number";
            const totalChanged =
              typeof beforeTotal === "number" &&
              hasAfterTotal &&
              beforeTotal !== afterTotal;
            // Show "before → after" when the count changed, otherwise show the
            // current count on its own (so the total is always visible).
            const totalSummary = totalChanged
              ? `${beforeTotal} → ${afterTotal}`
              : hasAfterTotal
                ? String(afterTotal)
                : typeof beforeTotal === "number"
                  ? String(beforeTotal)
                  : null;

            return [
              <div key={field} className="audit-log-field audit-log-field--update">
                <div className="audit-log-field-label">
                  {renderFieldLabel(field)}
                </div>
                {totalSummary !== null ? (
                  <div className="audit-log-affected-summary">
                    <Text
                      size="0.8125rem"
                      weight="500"
                      color="#475467"
                      text={`${fieldMap.totalStudents || "Students Scheduled"}:`}
                    />
                    <Text
                      size="0.8125rem"
                      weight="600"
                      color="#101828"
                      text={`${totalSummary} students`}
                    />
                  </div>
                ) : null}
                <div className="audit-log-field-value">{affectedStudentItems}</div>
              </div>,
            ];
          }

          if (module === "stops" && field === "associatedChilds") {
            const partition = partitionAssociatedChildsChanges(
              beforeData?.associatedChilds,
              afterData?.associatedChilds,
            );
            const result = [];

            if (partition.tripSessionOnly.length > 0) {
              const orderedTripSession = [...partition.tripSessionOnly].sort((a, b) =>
                String(a?.childName ?? "").localeCompare(String(b?.childName ?? "")),
              );

              orderedTripSession.forEach((child) => {
                result.push(
                  <div
                    key={`trip-session-${child.childId}`}
                    className="audit-log-field audit-log-field--update"
                  >
                    <div className="audit-log-field-label">Trip Session Change</div>
                    <div className="audit-log-field-value">
                      <div className="audit-log-schedule-item">
                        <Text
                          size="0.875rem"
                          weight="500"
                          color="#101828"
                          text={child.childName || "Unknown student"}
                        />
                        {renderDiffBoxes(
                          formatTripSessionLabel(child.beforeTripSession),
                          formatTripSessionLabel(child.afterTripSession),
                        )}
                      </div>
                    </div>
                  </div>,
                );
              });
            }

            if (partition.hasListChanges) {
              result.push(
                renderUpdateField(
                  field,
                  renderAssociatedChildsList(beforeData?.associatedChilds),
                  renderAssociatedChildsList(afterData?.associatedChilds),
                ),
              );
            }

            if (result.length > 0) return result;

            const beforeValue = formatValue(
              getFieldValue(beforeData, field),
              field,
              beforeData,
            );
            const afterValue = formatValue(
              getFieldValue(afterData, field),
              field,
              afterData,
            );

            if (beforeValue === null && afterValue === null) return [];
            return [renderUpdateField(field, beforeValue, afterValue)];
          }

          if (module === "drivers" && field === "passwordChange") {
            return [renderOccurredField(field, "Yes")];
          }

          const modulesRow = renderAdminModulesField(field, "update", {
            beforeData,
            afterData,
          });
          if (modulesRow) return [modulesRow];

          const beforeRecord =
            module === "campaigns" && field === "targetAudience"
              ? mergeCampaignAudienceRecord(beforeData, afterData)
              : beforeData;
          const afterRecord =
            module === "campaigns" && field === "targetAudience"
              ? mergeCampaignAudienceRecord(afterData, beforeData)
              : afterData;

          const beforeValue = formatValue(
            getFieldValue(beforeRecord, field),
            field,
            beforeRecord,
          );
          const afterValue = formatValue(
            getFieldValue(afterRecord, field),
            field,
            afterRecord,
          );

          if (beforeValue === null && afterValue === null) return [];
          return [renderUpdateField(field, beforeValue, afterValue)];
        })
        .filter(Boolean);

      if (rows.length === 0) {
        return <p className="audit-logs-empty">No changes detected.</p>;
      }

      return <div className="audit-logs-changes">{rows}</div>;
    }

    return <p className="audit-logs-empty">Unable to display this audit event.</p>;
  };

  return (
    <div className="modal-backdrop">
      <Box
        sx={boxStyle}
        className="audit-logs-modal-box"
      >
        <div className="user-modal-header audit-logs-modal-header">
          <div className="user-modal-header-contents">
            <div className="audit-logs-modal-header-row">
              <div className="audit-logs-modal-header-leading">
                <div className="icon-container audit-logs-modal-icon">
                  <img
                    style={{ margin: "auto" }}
                    src={AuditLogIcon}
                    alt=""
                    loading="lazy"
                    aria-hidden="true"
                  />
                </div>
                <div className="user-modal-header-title">
                  <Title
                    title={getTitle()}
                    subtitle={getSubtitle()}
                    titleSize="1.125rem"
                    subtitleSize="0.875rem"
                    gap="0.25rem"
                    lineHeight="1.75rem"
                  />
                </div>
              </div>
              {recordName && (
                <div className="audit-logs-record-badge">
                  <Badge
                    width="fit-content"
                    text={recordName}
                    background="#F2F4F7"
                    color="#344054"
                    tooltip={`${recordLabel}: ${recordName}`}
                    showInfoDot
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="audit-logs-meta">
          <Badge
            width="fit-content"
            text={formatCapitalize(actionType)}
            {...getActionBadgeProps(actionType)}
          />
          <Badge
            width="fit-content"
            text={moduleLabel}
            background="#F2F4F7"
            color="#344054"
          />
          <span className="audit-logs-meta-divider" aria-hidden="true" />
          <div className="audit-logs-meta-user">
            <Text
              text={selectedData.performedBy?.name || "Unknown user"}
              size="0.875rem"
              weight="500"
              color="#101828"
            />
            <Text
              text={formatCapitalize(selectedData.performedBy?.role || "Unknown role")}
              size="0.75rem"
              weight="400"
              color="#667085"
            />
          </div>
          <div className="audit-logs-meta-timestamp">
            <Text
              text={formattedDate}
              size="0.875rem"
              weight="500"
              color="#101828"
            />
            <Text
              text={formattedTime}
              size="0.75rem"
              weight="400"
              color="#667085"
            />
          </div>
        </div>

        <div className="user-modal-content audit-logs-modal-content">
          {renderChanges()}
        </div>

        <div className="button-container-delete audit-logs-modal-actions">
          <SecondaryButton
            label="Close"
            width="100%"
            imageIcon={false}
            onClickFunction={onClose}
          />
        </div>
      </Box>
    </div>
  );
};
